// apps/client/src/components/reports/VarianceLoanTable.tsx
// Per-loan drill-down for /reports/approved-vs-settled. Sortable, paginated,
// row-click opens the detail/annotation slot in a modal owned by the page.

import React, { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, MinusCircle, HardHat } from 'lucide-react';
import { formatCurrencyShorthand } from '../../utils/formatters';
import type { VarianceLoanRow } from '../../hooks/reports/useApprovedVsSettledVariance';

type SortKey =
  | 'customerName'
  | 'lender'
  | 'brokerDisplay'
  | 'settlementDate'
  | 'approvedCents'
  | 'settledCents'
  | 'subsequentAmountCents'
  | 'shortfallPct'
  | 'upfrontActualCents'
  | 'upfrontImpliedFullCents'
  | 'upfrontDecreaseCents';

interface VarianceLoanTableProps {
  rows: VarianceLoanRow[];
  loading?: boolean;
  onRowClick?: (loan: VarianceLoanRow) => void;
  /** Show the top-up column (tenant approved_vs_settled_subsequent flag observe/enforce). */
  showSubsequent?: boolean;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export const VarianceLoanTable: React.FC<VarianceLoanTableProps> = ({ rows, loading, onRowClick, showSubsequent = false }) => {
  const [sortKey, setSortKey] = useState<SortKey>('upfrontDecreaseCents');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(25);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let cmp = 0;
      const av = (a as any)[sortKey];
      const bv = (b as any)[sortKey];
      if (typeof av === 'string' || typeof bv === 'string') {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''));
      } else {
        cmp = (Number(av) || 0) - (Number(bv) || 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visible = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(['customerName', 'lender', 'brokerDisplay', 'settlementDate'].includes(key) ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const SortIcon: React.FC<{ active: boolean; dir: 'asc' | 'desc' }> = ({ active, dir }) => {
    if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  if (!loading && rows.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <p className="text-gray-400 text-sm">No loans match the current filter.</p>
        <p className="text-gray-500 text-xs mt-1">Try toggling "Decreased only" off, or adjust the date range.</p>
      </div>
    );
  }

  const headerCell = (label: string, key: SortKey, align: 'left' | 'right' = 'right') => (
    <th
      className={`py-2 px-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition whitespace-nowrap ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={() => toggleSort(key)}
    >
      <span className={`inline-flex items-center gap-1.5 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <SortIcon active={sortKey === key} dir={sortDir} />
      </span>
    </th>
  );

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: showSubsequent ? 1200 : 1100 }}>
          <thead className="bg-gray-900/40">
            <tr>
              {headerCell('Customer', 'customerName', 'left')}
              {headerCell('Lender', 'lender', 'left')}
              {headerCell('Broker', 'brokerDisplay', 'left')}
              {headerCell('Settled', 'settlementDate', 'left')}
              {headerCell('Approved', 'approvedCents')}
              {headerCell('Settled $', 'settledCents')}
              {showSubsequent && headerCell('Top-up', 'subsequentAmountCents')}
              {headerCell('Short %', 'shortfallPct')}
              {headerCell('Upfront paid', 'upfrontActualCents')}
              {headerCell('Implied', 'upfrontImpliedFullCents')}
              {headerCell('Decrease', 'upfrontDecreaseCents')}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {visible.map(loan => {
              const dimmed = loan.isUserExcluded;
              return (
                <tr
                  key={loan.loanId}
                  className={`hover:bg-gray-700/30 transition cursor-pointer ${dimmed ? 'opacity-60' : ''}`}
                  onClick={() => onRowClick?.(loan)}
                >
                  <td className="py-2.5 px-3 text-gray-200 font-medium whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span>{loan.customerName || <span className="text-gray-500 italic">—</span>}</span>
                      {loan.isConstructionLikely && (
                        <span
                          className="inline-flex items-center gap-1 text-[0.625rem] font-medium px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 whitespace-nowrap"
                          title="This loan looks like a construction or progressive-draw — additional upfront commission may still come through as the borrower draws further on the facility."
                        >
                          <HardHat className="w-2.5 h-2.5" />
                          Construction
                        </span>
                      )}
                      {loan.isUserExcluded && (
                        <span
                          className="inline-flex items-center gap-1 text-[0.625rem] font-medium px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-400"
                          title={loan.userAnnotationReason || 'Excluded from totals by an admin'}
                        >
                          <MinusCircle className="w-2.5 h-2.5" />
                          Excluded
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-gray-300 whitespace-nowrap">{loan.lender || '—'}</td>
                  <td className="py-2.5 px-3 text-gray-300 whitespace-nowrap">{loan.brokerDisplay || '—'}</td>
                  <td className="py-2.5 px-3 text-gray-400 tabular-nums whitespace-nowrap">{loan.settlementDate || '—'}</td>
                  <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{formatCurrencyShorthand(loan.approvedCents)}</td>
                  <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{formatCurrencyShorthand(loan.settledCents)}</td>
                  {showSubsequent && (
                    <td
                      className="py-2.5 px-3 text-right text-gray-300 tabular-nums"
                      title={loan.subsequentAmountCents > 0
                        ? `Subsequent upfront of ${formatCurrencyShorthand(loan.subsequentUpfrontCents)} paid on this top-up`
                        : undefined}
                    >
                      {loan.subsequentAmountCents > 0 ? formatCurrencyShorthand(loan.subsequentAmountCents) : '—'}
                    </td>
                  )}
                  <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{(loan.shortfallPct * 100).toFixed(1)}%</td>
                  <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{formatCurrencyShorthand(loan.upfrontActualCents)}</td>
                  <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{formatCurrencyShorthand(loan.upfrontImpliedFullCents)}</td>
                  <td className="py-2.5 px-3 text-right font-semibold tabular-nums" style={{
                    color: loan.upfrontDecreaseCents > 0 ? '#f87171' : '#9ca3af'
                  }}>
                    {formatCurrencyShorthand(loan.upfrontDecreaseCents)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700/50 text-sm">
        <div className="flex items-center gap-3 text-gray-400">
          <span>
            {sorted.length === 0
              ? '0 results'
              : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, sorted.length)} of ${sorted.length}`}
          </span>
          <select
            className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-gray-300"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value) as (typeof PAGE_SIZE_OPTIONS)[number]); setPage(1); }}
          >
            {PAGE_SIZE_OPTIONS.map(s => (
              <option key={s} value={s}>{s}/page</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={safePage <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1 text-xs rounded bg-gray-700 text-gray-300 disabled:opacity-30 hover:bg-gray-600 transition"
          >
            Prev
          </button>
          <span className="text-xs text-gray-400 tabular-nums">{safePage} / {totalPages}</span>
          <button
            disabled={safePage >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="px-3 py-1 text-xs rounded bg-gray-700 text-gray-300 disabled:opacity-30 hover:bg-gray-600 transition"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default VarianceLoanTable;
